/**
 * <s-delivery-estimate> turns a business-day window (dispatch + carrier transit,
 * written by Liquid from theme settings) into a calendar date range for the
 * shopper. Weekends are skipped; day 1 is the next business day. If the window
 * is missing, the server-rendered fallback text stays as is.
 *
 * Attributes: data-min, data-max (total business days), data-locale.
 * Children: [data-estimate-range] receives the formatted range;
 * [data-estimate-ready] is unhidden and [data-estimate-fallback] hidden on success.
 */
class SDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const min = Number.parseInt(this.dataset.min ?? '', 10);
    const max = Number.parseInt(this.dataset.max ?? '', 10);
    const output = this.querySelector('[data-estimate-range]');
    if (!output || !Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max < min) return;

    const start = addBusinessDays(new Date(), min);
    const end = addBusinessDays(new Date(), max);
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' });

    output.textContent =
      min === max || typeof format.formatRange !== 'function'
        ? min === max
          ? format.format(end)
          : `${format.format(start)} – ${format.format(end)}`
        : format.formatRange(start, end);

    this.querySelector('[data-estimate-ready]')?.removeAttribute('hidden');
    this.querySelector('[data-estimate-fallback]')?.setAttribute('hidden', '');
  }
}

/** @param {Date} from @param {number} days */
function addBusinessDays(from, days) {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', SDeliveryEstimate);
}
